'use client'

import { Flex, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import SearchableSelect from '@/components/basic/SearchableSelect'
import { DEFAULT_CURRENCY, getCurrencyInfo, supportedCurrencies } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { setSelectedCurrency } from '@/server/actions'
import type { Currency } from '@/types/global'

export default function CurrencySelect() {
  const { selectedCurrency: selectedCurrencyCode } = useSsrContext()
  const t = useTranslations('currencySelector')
  const currentCurrency = useMemo(
    () => getCurrencyInfo(selectedCurrencyCode) || DEFAULT_CURRENCY,
    [selectedCurrencyCode],
  )

  async function handleCurrencyChange(currency: Currency) {
    await setSelectedCurrency(currency.code)
  }

  return (
    <SearchableSelect
      options={supportedCurrencies.sort((a, b) => a.name.localeCompare(b.name))}
      value={currentCurrency}
      showSelectedFirst={true}
      onChange={handleCurrencyChange}
      getOptionKey={item => item.code}
      getOptionLabel={item => item.code}
      renderOptionContent={(item, _isSelected) => (
        <Flex gap="0" direction="column">
          <Text variant="body" lineClamp={2}>
            {item.code}
          </Text>
          <Text variant="subtitle" maw="200px" c="var(--content-secondary)" truncate>
            {t(item.nameKey || '') || item.name}
          </Text>
        </Flex>
      )}
      searchFilter={(item, search) =>
        item.code.toLowerCase().includes(search) || item.name.toLowerCase().includes(search)
      }
      searchPlaceholder="Search"
    />
  )
}
